// Template 9 — "Vivid Dark" — the dark-theme sibling of Template 2: near-black
// canvas, glowing gradient mesh, Bricolage Grotesque display, bento hero, marquee
// ticker, kinetic gradient headline and vibrant gradient bands. Fully theme-driven.
import { Fragment, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, HeroVideo, SOCIAL_ICONS, orderedSections, skillIconD, PolicyLinks, GuaranteeBadge, Reveal, LessonIcon } from "./shared";

const SIGNATURE2 = "#F97316";
const SIGNATURE = "#3B82F6";     // electric blue — premium glow on near-black
const BG = "#0b0b12";            // near-black canvas
const CARD = "#15151e";          // dark card
const FOOT = "#08080d";          // footer
const LINE = "rgba(255,255,255,.09)";
const DISPLAY = "'Bricolage Grotesque', system-ui, sans-serif";
const SANS = "'Inter', system-ui, sans-serif";

export default function Template9({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies }) {
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&display=swap");
  const vid = usePreviewVideo();
  const A = themeColor || SIGNATURE;
  const AD = shade(A, -0.22);
  const AL = shade(A, 0.4);
  const B = themeColor2 || SIGNATURE2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, AL, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);

  const order = orderedSections(landing.section_order);
  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t9-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t9-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t9-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t9-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t9-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t9-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  const tiles = [
    course.star_rating > 0 && { k: "Rating", v: Number(course.star_rating).toFixed(1), sub: labels.rating || "out of 5" },
    course.total_lessons > 0 && { k: "Lessons", v: course.total_lessons, sub: labels.lessons || "on-demand" },
    modules.length > 0 && { k: "Modules", v: modules.length, sub: labels.modules || "chapters" },
  ].filter(Boolean).slice(0, 2);

  const marquee = [`${lessonCount} ${labels.lessons || "Lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "Modules"}`, "Lifetime access", "Self-paced", "Learn anywhere", statusLabel].filter(Boolean);

  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal><Sec id="t9-about" kicker="Overview" title="About this course" pal={pal}>
        <div className="rich-content mt-6 max-w-none text-[15px] leading-relaxed text-white/70"><RichText html={landing.description || course.description} /></div>
      </Sec></Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal><div id="t9-instructor"><Sec kicker="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
        <div className="relative mt-8 overflow-hidden rounded-[2rem] border p-6 sm:p-8" style={{ background: CARD, borderColor: LINE }}>
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-70 blur-3xl" style={{ background: rgba(A, 0.4) }} />
          <div className="relative grid items-center gap-8 sm:grid-cols-[210px_1fr]">
            <div className="relative mx-auto w-full max-w-[210px]">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem]" style={{ border: `1px solid ${LINE}`, boxShadow: `0 26px 52px -24px ${rgba(A, 0.6)}` }}>
                {instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-6xl font-bold text-white" style={{ fontFamily: DISPLAY, background: BG }}>{instructor.name.charAt(0).toUpperCase()}</span>}
              </div>
              <span className="absolute -bottom-3 -right-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: grad, boxShadow: `0 12px 26px ${rgba(A, 0.6)}` }}><Icon d="M4.5 12.75l6 6 9-13.5" className="h-5 w-5" stroke={2.4} /></span>
            </div>
            <div className="text-center sm:text-left">
              <span className="text-[0.72rem] font-bold uppercase tracking-[0.16em]" style={{ color: A }}>Course Instructor</span>
              <h3 className="mt-1.5 text-[1.9rem] font-[700] text-white" style={{ fontFamily: DISPLAY }}>{instructor.name}</h3>
              <span className="mx-auto mt-3 block h-1 w-12 rounded-full sm:mx-0" style={{ background: grad }} />
              {instructor.bio && <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/60">{instructor.bio}</p>}
            </div>
          </div>
        </div>
      </Sec></div></Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal><Sec id="t9-skill" kicker={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {landing.skill.list.map((s, i) => (
            <div key={i} className="group flex items-start gap-4 rounded-3xl border p-5 transition-all duration-300 hover:-translate-y-1" style={{ background: CARD, borderColor: LINE }}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: grad, boxShadow: `0 8px 20px ${rgba(A, 0.45)}` }}><Icon d={skillIconD(landing.skill.icon)} className="h-5 w-5" stroke={2.2} /></span>
              <span className="pt-1.5 text-[15px] font-medium leading-snug text-white/85">{s}</span>
            </div>
          ))}
        </div>
        {landing.skill.long_desc && <div className="rich-content mt-6 text-[15px] leading-relaxed text-white/70" dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
      </Sec></Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t9-curriculum"><Sec kicker={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}>
        <Accordion modules={modules} pal={pal} labels={labels} />
      </Sec></div></Reveal>
    ),
    testimonial: <div id="t9-reviews"><Testimonials testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <Guarantee guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t9-faq"><Faq faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="min-h-screen text-white" style={{ background: BG, fontFamily: SANS }}>
      {/* NAV */}
      <div className="sticky top-0 z-50 px-3 pt-3 sm:px-5">
        <nav className="mx-auto max-w-6xl">
          <div className="flex h-14 items-center justify-between gap-3 rounded-full border px-3 pl-5 shadow-[0_10px_40px_rgba(0,0,0,.5)] backdrop-blur-xl" style={{ background: "rgba(21,21,30,.72)", borderColor: LINE }}>
            <a href="#top" className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl text-white" style={{ background: grad, boxShadow: `0 6px 16px ${rgba(A, 0.5)}` }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
              <span className="max-w-[38vw] truncate text-[15px] font-bold tracking-tight text-white sm:max-w-xs" style={{ fontFamily: DISPLAY }}>{course.name}</span>
            </a>
            <div className="hidden items-center gap-1 md:flex">
              {navLinks.map((l) => <a key={l.href} href={l.href} className="rounded-full px-3.5 py-2 text-[13.5px] font-semibold text-white/55 transition hover:bg-white/[.06] hover:text-white">{l.label}</a>)}
            </div>
            <button onClick={onEnroll} className="group relative overflow-hidden rounded-full px-5 py-2 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 6px 18px ${rgba(A, 0.5)}` }}>
              <Shine /> <span className="relative">{ctaLabel}</span>
            </button>
          </div>
        </nav>
      </div>

      {/* HERO — bento */}
      <header id="top" className="relative overflow-hidden">
        <MeshBg A={A} AD={AD} B={B} />
        {header.bg_image ? <><img src={header.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /><div className="pointer-events-none absolute inset-0" style={{ background: `linear-gradient(to bottom, ${rgba(BG, 0.4)}, ${BG})` }} /></> : null}
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pb-14 pt-14 sm:px-6 lg:grid-cols-[1.05fr_.95fr] lg:gap-12 lg:px-8 lg:pb-20 lg:pt-20">
          <div>
            <span className="inline-flex animate-fade-in-up items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider backdrop-blur" style={{ color: A, background: "rgba(255,255,255,.05)", borderColor: LINE }}>
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: A }} /><span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: A }} /></span>
              {statusLabel}
            </span>
            <h1 className="mt-5 animate-fade-in-up text-[2.9rem] font-[700] leading-[0.98] tracking-[-0.03em] text-white sm:text-6xl lg:text-[4.3rem]" style={{ fontFamily: DISPLAY, animationDelay: "60ms" }}>
              {t.before}{" "}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, ${A}, ${B}, ${A})`, backgroundSize: "200% 100%", animation: "gradientPan 5s ease infinite" }}>{t.accent}</span>{" "}
              {t.after}
            </h1>
            {sub && <p className="mt-6 max-w-lg animate-fade-in-up text-[16px] leading-relaxed text-white/60 sm:text-[17px]" style={{ animationDelay: "120ms" }}>{sub}</p>}
            <div className="mt-8 flex animate-fade-in-up flex-wrap items-center gap-3" style={{ animationDelay: "180ms" }}>
              <button onClick={onEnroll} className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 16px 36px ${rgba(A, 0.5)}` }}>
                <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#t9-curriculum" className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-[15px] font-semibold text-white/85 backdrop-blur transition hover:bg-white/5" style={{ borderColor: LINE, background: "rgba(255,255,255,.03)" }}>{labels.preview || "Preview Course"}</a>
            </div>
          </div>

          {landing.header?.show !== false && (
            <div className="animate-fade-in-up" style={{ animationDelay: "160ms" }}>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="group relative col-span-2 animate-float overflow-hidden rounded-[1.75rem] border p-2 shadow-[0_30px_70px_-30px_rgba(0,0,0,.8)] backdrop-blur-xl" style={{ background: "rgba(21,21,30,.7)", borderColor: LINE }}>
                  <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[36px] opacity-90 blur-2xl" style={{ background: `radial-gradient(60% 60% at 50% 40%, ${rgba(A, 0.4)}, transparent 70%)` }} />
                  <div className="overflow-hidden rounded-[1.35rem]">
                    <HeroVideo video={landing.video} poster={course.course_image} accent={A} className="aspect-video w-full" style={{ background: "#000" }} onExpand={vid.play} />
                  </div>
                </div>
                {tiles.map((tl, i) => (
                  <div key={i} className={`rounded-[1.5rem] border p-5 shadow-[0_12px_30px_-18px_rgba(0,0,0,.6)] backdrop-blur-xl transition-transform hover:-translate-y-1 ${tiles.length === 1 ? "col-span-2" : ""}`} style={{ background: "rgba(21,21,30,.7)", borderColor: LINE }}>
                    <div className="flex items-center gap-2">
                      {tl.k === "Rating" ? <Stars value={Number(tl.v)} color={B} empty="rgba(255,255,255,.16)" className="h-4 w-4" /> : <span className="h-2 w-2 rounded-full" style={{ background: A }} />}
                    </div>
                    <div className="mt-3 text-4xl font-[700] tracking-tight text-white" style={{ fontFamily: DISPLAY }}>{tl.v}</div>
                    <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/50">{tl.k} · {tl.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* MARQUEE */}
      <div className="relative overflow-hidden border-y backdrop-blur" style={{ background: "rgba(255,255,255,.03)", borderColor: LINE }}>
        <div className="flex w-max animate-marquee whitespace-nowrap py-3.5">
          {[0, 1].map((r) => (
            <div key={r} className="flex shrink-0 items-center">
              {marquee.map((m, i) => (
                <span key={i} className="flex items-center gap-6 pr-6 text-[13px] font-bold uppercase tracking-[0.14em] text-white/55">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: A }} />{m}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        {/* ENROLL PANEL — full width */}
        <Reveal>
          <div className="mb-16 overflow-hidden rounded-[2rem] border shadow-[0_30px_70px_-45px_rgba(0,0,0,.8)] lg:mb-20" style={{ background: CARD, borderColor: LINE }}>
            <div className="grid gap-6 p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-9 sm:p-8">
              <div className="text-center sm:border-r sm:pr-9 sm:text-left" style={{ borderColor: LINE }}>
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: A }}>{statusLabel}</span>
                <div className="mt-1 flex items-baseline justify-center gap-2 sm:justify-start">
                  <span className="bg-clip-text text-5xl font-[700] leading-none tracking-tight text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${A}, ${AL})`, fontFamily: DISPLAY }}>{priceLabel}</span>
                  {isPaid && <span className="text-sm text-white/40">one-time</span>}
                </div>
              </div>
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2.5 sm:justify-center">
                {[course.total_lessons > 0 && `${course.total_lessons} ${labels.lessons || "lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "modules"}`, "Lifetime access", "Learn on any device"].filter(Boolean).map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium text-white/70"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white" style={{ background: grad }}><Icon d="M4.5 12.75l6 6 9-13.5" className="h-3 w-3" stroke={2.6} /></span>{it}</li>
                ))}
              </ul>
              <button onClick={onEnroll} className="group relative flex items-center justify-center gap-2 overflow-hidden rounded-full px-8 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" style={{ background: grad, boxShadow: `0 14px 32px ${rgba(A, 0.5)}` }}>
                <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            {isPaid && landing.guarantee?.show && (
              <div className="border-t px-6 py-3.5 text-center text-[13px] sm:px-8" style={{ borderColor: LINE, background: rgba(A, 0.08) }}>
                <span className="font-bold text-white">{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> <span className="text-white/60">{labels.not_satisfied || "Not satisfied? Get a full refund."}</span>
              </div>
            )}
          </div>
        </Reveal>

        {/* SECTIONS — full width */}
        <div className="space-y-20">
          {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
        </div>

        {/* FINAL CTA — vivid gradient card */}
        {landing.cta?.show !== false && (
          <Reveal>
            <section className="relative mt-16 overflow-hidden rounded-[2.5rem] px-6 py-16 text-center sm:px-12" style={{ background: grad }}>
              <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: `radial-gradient(60% 80% at 15% 20%, rgba(255,255,255,.5), transparent 55%), radial-gradient(60% 80% at 90% 90%, ${rgba(AD, 0.7)}, transparent 55%)`, backgroundSize: "180% 180%", animation: "gradientPan 8s ease infinite" }} />
              <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: `radial-gradient(rgba(255,255,255,.9) 1px, transparent 1px)`, backgroundSize: "22px 22px" }} />
              <div className="relative">
                <h2 className="text-3xl font-[700] tracking-tight text-white sm:text-[2.8rem]" style={{ fontFamily: DISPLAY }}>{landing.cta?.heading || "Ready to get started?"}</h2>
                <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-white/80">{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
                <button onClick={onEnroll} className="group relative mt-8 inline-flex items-center gap-2 overflow-hidden rounded-full bg-white px-8 py-4 text-[15px] font-bold shadow-xl transition-transform duration-200 hover:-translate-y-0.5" style={{ color: AD }}>
                  <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </section>
          </Reveal>
        )}
      </div>

      {/* FOOTER */}
      <footer className="border-t" style={{ background: FOOT, borderColor: LINE }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-4 py-10 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition hover:-translate-y-0.5 hover:text-white" style={{ background: "rgba(255,255,255,.07)" }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={A} dark />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}
function Shine() {
  return <span className="pointer-events-none absolute inset-y-0 -left-4 w-1/3 animate-shine bg-white/25 blur-md" aria-hidden />;
}
function MeshBg({ A, AD, B }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0" style={{ background: BG }} />
      <div className="absolute -left-40 -top-40 h-[34rem] w-[34rem] animate-float-slow rounded-full opacity-40 blur-[110px]" style={{ background: rgba(A, 0.5) }} />
      <div className="absolute right-[-10rem] top-16 h-[30rem] w-[30rem] animate-float rounded-full opacity-30 blur-[110px]" style={{ background: rgba(AD, 0.5) }} />
      <div className="absolute bottom-[-12rem] left-1/3 h-[26rem] w-[26rem] animate-float-slow rounded-full opacity-25 blur-[120px]" style={{ background: rgba(B, 0.45) }} />
      <div className="absolute inset-0 opacity-50" style={{ backgroundImage: `radial-gradient(${rgba(A, 0.1)} 1px, transparent 1px)`, backgroundSize: "22px 22px", maskImage: "radial-gradient(80% 60% at 50% 20%, #000, transparent 78%)", WebkitMaskImage: "radial-gradient(80% 60% at 50% 20%, #000, transparent 78%)" }} />
    </div>
  );
}

function Sec({ id, kicker, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white" style={{ background: pal.grad, boxShadow: `0 6px 16px ${rgba(pal.A, 0.4)}` }}>{kicker}</span>
      <h2 className="mt-4 text-[2.1rem] font-[700] leading-[1.04] tracking-[-0.02em] text-white sm:text-[2.8rem]" style={{ fontFamily: DISPLAY }}>{title}</h2>
      {sub && <p className="mt-2 text-[15px] text-white/55">{sub}</p>}
      {children}
    </section>
  );
}

function Accordion({ modules, pal, labels }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-8 space-y-3">
      {modules.map((m, mi) => {
        const isOpen = open === mi;
        return (
          <div key={mi} className="overflow-hidden rounded-3xl border transition-all duration-300" style={isOpen ? { background: CARD, borderColor: rgba(pal.A, 0.45), boxShadow: `0 20px 44px -28px ${rgba(pal.A, 0.6)}` } : { background: CARD, borderColor: LINE }}>
            <button onClick={() => setOpen(isOpen ? -1 : mi)} className="flex w-full items-center gap-4 px-5 py-4 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white transition" style={isOpen ? { background: pal.grad, boxShadow: `0 8px 20px ${rgba(pal.A, 0.45)}` } : { background: rgba(pal.A, 0.14), color: pal.A }}>{String(mi + 1).padStart(2, "0")}</span>
              <span className="flex-1 text-[15px] font-bold text-white" style={{ fontFamily: DISPLAY }}>{m.name}</span>
              <span className="hidden text-xs font-medium text-white/45 sm:block">{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: rgba(pal.A, 0.14) }}><Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4" style={{ color: pal.A }} /></span>
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
              <div className="overflow-hidden">
                <ul className="px-5 pb-2">
                  {(m.lessons || []).map((l, li) => (
                    <li key={li} className="flex items-center gap-3 border-t py-3" style={{ borderColor: LINE }}>
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: l.locked ? "rgba(255,255,255,.06)" : rgba(pal.A, 0.14) }}>
                        <LessonIcon lesson={l} color={pal.A} muted="rgba(255,255,255,.35)" className="h-3.5 w-3.5" />
                      </span>
                      <span className={`flex-1 truncate text-sm ${l.locked ? "text-white/40" : "text-white/80"}`}>{l.title}</span>
                      {l.completed && <span className="text-xs font-semibold" style={{ color: pal.A }}>Done</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Testimonials({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="group relative flex flex-col overflow-hidden rounded-[1.75rem] border p-6 transition-all duration-300 hover:-translate-y-1" style={{ background: CARD, borderColor: LINE }}>
              <span className="pointer-events-none absolute -right-3 -top-4 text-[7rem] font-black leading-none" aria-hidden style={{ fontFamily: DISPLAY, color: rgba(pal.A, 0.18) }}>&rdquo;</span>
              <Stars value={t.rating} empty="rgba(255,255,255,.16)" color={pal.B} />
              <blockquote className="relative mt-4 flex-1 text-[15px] leading-relaxed text-white/70">{t.quote}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t pt-5" style={{ borderColor: LINE }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-sm font-bold text-white" style={{ background: pal.grad }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-bold text-white">{t.name}</span>{t.role && <span className="block truncate text-xs text-white/45">{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function Guarantee({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden rounded-[2rem] px-6 py-11 sm:px-10" style={{ background: pal.grad }}>
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: `radial-gradient(60% 100% at 12% 30%, rgba(255,255,255,.5), transparent 55%), radial-gradient(60% 100% at 92% 80%, ${rgba(pal.AD, 0.8)}, transparent 55%)`, backgroundSize: "180% 180%", animation: "gradientPan 9s ease infinite" }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /> : null}
        <div className="relative flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-2xl font-[700] text-white sm:text-[1.9rem]" style={{ fontFamily: DISPLAY }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed text-white/85" dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image
              ? <img src={guarantee.image} alt="" className="h-44 w-44 object-contain drop-shadow-2xl" />
              : <div className="rounded-full bg-white p-3 shadow-[0_20px_50px_-16px_rgba(0,0,0,.55)]"><GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-40 w-40 sm:h-44 sm:w-44" /></div>}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function Faq({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-8 space-y-3">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="overflow-hidden rounded-3xl border transition-all duration-300" style={isOpen ? { background: CARD, borderColor: rgba(pal.A, 0.45), boxShadow: `0 18px 40px -26px ${rgba(pal.A, 0.6)}` } : { background: CARD, borderColor: LINE }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-4 px-5 py-4 text-left">
                  <span className="flex-1 text-[15px] font-bold text-white" style={{ fontFamily: DISPLAY }}>{f.q}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition" style={isOpen ? { background: pal.grad } : { background: rgba(pal.A, 0.14) }}>
                    <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4" style={{ color: isOpen ? "#fff" : pal.A }} />
                  </span>
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="px-5 pb-5 text-[15px] leading-relaxed text-white/70">{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
